import React, { useEffect, useState } from "react";

import {
  FaChevronDown,
  FaChevronUp,
  FaChromecast,
  FaCopy,
  FaEdit,
  FaImage,
  FaPowerOff,
  FaTrash,
  FaTrophy,
} from "react-icons/fa";

import {
  formatDateRangeFR,
  parseEventCategories,
  shortText,
} from "../utils/eventUtils";

function EventStatusPill({ active }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${
        active
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-emerald-500" : "bg-rose-500"
        }`}
      />

      {active ? "Actif" : "Inactif"}
    </span>
  );
}

function DisciplinePill({ discipline }) {
  if (!discipline) {
    return <span className="text-slate-400">—</span>;
  }

  return (
    <span className="inline-flex rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300">
      {discipline}
    </span>
  );
}

function AgeChips({ items = [], max = 3 }) {
  if (!items.length) {
    return <span className="text-slate-400">—</span>;
  }

  const visibleItems = items.slice(0, max);
  const remainingCount = items.length - visibleItems.length;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleItems.map((category) => (
        <span
          key={category}
          className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-800 ring-1 ring-sky-200 dark:bg-sky-900/40 dark:text-sky-200 dark:ring-sky-800/50"
        >
          {category}
        </span>
      ))}

      {remainingCount > 0 ? (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
          +{remainingCount}
        </span>
      ) : null}
    </div>
  );
}

function EventThumb({ src, alt }) {
  const [imageSrc, setImageSrc] = useState(src || "");

  useEffect(() => {
    setImageSrc(src || "");
  }, [src]);

  if (!imageSrc) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-400 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-700">
        —
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt || "Événement"}
      onError={() => setImageSrc("")}
      className="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
    />
  );
}

function EventIdentity({ event }) {
  return (
    <div className="flex items-start gap-3">
      <EventThumb src={event.photo_url} alt={event.titre} />

      <div className="min-w-0">
        <div className="text-[15px] font-semibold leading-5 text-slate-900 dark:text-white">
          {event.titre || "Sans titre"}
        </div>

        {event.description ? (
          <div className="mt-1 line-clamp-2 text-[12px] text-slate-500 dark:text-slate-400">
            {event.description}
          </div>
        ) : null}

        <div className="mt-2 text-[11px] font-medium text-slate-500 dark:text-slate-400">
          {formatDateRangeFR(event.start, event.end)}
        </div>
      </div>
    </div>
  );
}

function EventActions({
  event,
  hasResults,
  onEdit,
  onDuplicate,
  onResults,
  onCasting,
  onImage,
  onDelete,
}) {
  return (
    <div className="flex flex-wrap justify-center gap-1.5">
      <button
        type="button"
        onClick={() => onEdit(event)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-600 text-white transition hover:bg-indigo-700"
        title="Modifier"
      >
        <FaEdit />
      </button>

      <button
        type="button"
        onClick={() => onDuplicate(event)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        title="Dupliquer"
      >
        <FaCopy />
      </button>

      <button
        type="button"
        onClick={() => onResults(event)}
        className={`inline-flex h-9 w-9 items-center justify-center rounded-xl transition ${
          hasResults
            ? "bg-orange-100 text-orange-800 ring-1 ring-orange-200 hover:bg-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20"
            : "border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
        }`}
        title={hasResults ? "Résultats disponibles" : "Créer les résultats"}
      >
        <FaTrophy />
      </button>

      <button
        type="button"
        onClick={() => onCasting(event)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-600 text-white transition hover:bg-sky-700"
        title="Ouvrir la page Casting"
      >
        <FaChromecast />
      </button>

      <button
        type="button"
        onClick={() => onImage(event)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        title="Changer l'image"
      >
        <FaImage />
      </button>

      <button
        type="button"
        onClick={() => onDelete(event)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-red-600 text-white transition hover:bg-red-700"
        title="Supprimer"
      >
        <FaTrash />
      </button>
    </div>
  );
}

function DesktopTable({
  rows,
  loading,
  hasResultsMap,
  onToggleActive,
  onEdit,
  onDuplicate,
  onResults,
  onCasting,
  onImage,
  onDelete,
}) {
  return (
    <div className="hidden overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 xl:block">
      {loading ? (
        <div className="px-4 py-12 text-center text-slate-500">
          Chargement des événements…
        </div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-12 text-center text-slate-500">
          Aucun événement ne correspond aux filtres.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1480px] table-fixed text-sm">
            <thead className="bg-slate-50 text-left dark:bg-slate-950/60">
              <tr className="text-slate-600 dark:text-slate-300">
                <th className="w-[20%] px-4 py-3 font-semibold">
                  Événement
                </th>
                <th className="w-[10%] px-4 py-3 font-semibold">
                  Discipline
                </th>
                <th className="w-[13%] px-4 py-3 font-semibold">
                  Type / niveau
                </th>
                <th className="w-[14%] px-4 py-3 font-semibold">
                  Catégories
                </th>
                <th className="w-[13%] px-4 py-3 font-semibold">
                  Organisateur
                </th>
                <th className="w-[12%] px-4 py-3 font-semibold">
                  Lieu
                </th>
                <th className="w-[8%] px-4 py-3 font-semibold">
                  Statut
                </th>
                <th className="w-[10%] px-4 py-3 text-center font-semibold">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {rows.map((event) => {
                const categories = parseEventCategories(event.cate_event);

                return (
                  <tr
                    key={event.id}
                    className="align-top transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                  >
                    <td className="px-4 py-3.5">
                      <EventIdentity event={event} />
                    </td>

                    <td className="px-4 py-3.5">
                      <DisciplinePill
                        discipline={event.discipline_event}
                      />
                    </td>

                    <td className="px-4 py-3.5 text-[13px] text-slate-700 dark:text-slate-200">
                      <div>{event.type_event || "—"}</div>

                      <div className="mt-1 text-slate-500 dark:text-slate-400">
                        {event.level_event || "—"}
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <AgeChips items={categories} />
                    </td>

                    <td className="px-4 py-3.5 text-[13px] text-slate-700 dark:text-slate-200">
                      {event.organisateur || "—"}
                    </td>

                    <td className="px-4 py-3.5 text-[13px] text-slate-700 dark:text-slate-200">
                      {event.lieu_event || "—"}
                    </td>

                    <td className="px-4 py-3.5">
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => onToggleActive(event)}
                          title={
                            event.is_active
                              ? "Désactiver l'événement"
                              : "Activer l'événement"
                          }
                          className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition ${
                            event.is_active
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                              : "border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          <FaPowerOff />
                        </button>

                        <EventStatusPill active={Boolean(event.is_active)} />
                      </div>
                    </td>

                    <td className="px-4 py-3.5">
                      <EventActions
                        event={event}
                        hasResults={Boolean(hasResultsMap[event.id])}
                        onEdit={onEdit}
                        onDuplicate={onDuplicate}
                        onResults={onResults}
                        onCasting={onCasting}
                        onImage={onImage}
                        onDelete={onDelete}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MobileCards({
  rows,
  loading,
  hasResultsMap,
  onToggleActive,
  onEdit,
  onDuplicate,
  onResults,
  onCasting,
  onImage,
  onDelete,
}) {
  const [expandedIds, setExpandedIds] = useState([]);

  const toggleExpanded = (id) => {
    setExpandedIds((previousIds) =>
      previousIds.includes(id)
        ? previousIds.filter((currentId) => currentId !== id)
        : [...previousIds, id]
    );
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 xl:hidden">
        Chargement des événements…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900 xl:hidden">
        Aucun événement ne correspond aux filtres.
      </div>
    );
  }

  return (
    <div className="space-y-3 xl:hidden">
      {rows.map((event) => {
        const expanded = expandedIds.includes(event.id);
        const categories = parseEventCategories(event.cate_event);

        return (
          <article
            key={event.id}
            className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
          >
            <EventIdentity event={event} />

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <EventStatusPill active={Boolean(event.is_active)} />

              <DisciplinePill discipline={event.discipline_event} />

              <AgeChips items={categories} max={2} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => onEdit(event)}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                <FaEdit />
                Modifier
              </button>

              <button
                type="button"
                onClick={() => toggleExpanded(event.id)}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {expanded ? <FaChevronUp /> : <FaChevronDown />}
                {expanded ? "Réduire" : "Détails"}
              </button>
            </div>

            {expanded ? (
              <div className="mt-4 border-t border-slate-100 pt-4 dark:border-slate-800">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Type / niveau
                      </div>

                      <div className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                        <div>{event.type_event || "—"}</div>

                        <div className="mt-1 text-slate-500 dark:text-slate-400">
                          {event.level_event || "—"}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Organisateur / lieu
                      </div>

                      <div className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                        <div>{event.organisateur || "—"}</div>

                        <div className="mt-1 text-slate-500 dark:text-slate-400">
                          {event.lieu_event || "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Description
                    </div>

                    <div className="text-sm leading-6 text-slate-700 dark:text-slate-200">
                      {shortText(event.description || "", 260) || "—"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => onToggleActive(event)}
                      className={`inline-flex h-10 items-center justify-center gap-2 rounded-2xl px-3 text-sm font-semibold transition ${
                        event.is_active
                          ? "bg-amber-500 text-white hover:bg-amber-600"
                          : "bg-emerald-600 text-white hover:bg-emerald-700"
                      }`}
                    >
                      <FaPowerOff />
                      {event.is_active ? "Désactiver" : "Activer"}
                    </button>

                    <button
                      type="button"
                      onClick={() => onDuplicate(event)}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaCopy />
                      Dupliquer
                    </button>

                    <button
                      type="button"
                      onClick={() => onResults(event)}
                      className={`inline-flex h-10 items-center justify-center gap-2 rounded-2xl px-3 text-sm font-medium transition ${
                        hasResultsMap[event.id]
                          ? "bg-orange-100 text-orange-800 ring-1 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20"
                          : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                      }`}
                    >
                      <FaTrophy />
                      Résultats
                    </button>

                    <button
                      type="button"
                      onClick={() => onCasting(event)}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-3 text-sm font-semibold text-white transition hover:bg-sky-700"
                    >
                      <FaChromecast />
                      Casting
                    </button>

                    <button
                      type="button"
                      onClick={() => onImage(event)}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaImage />
                      Image
                    </button>

                    <button
                      type="button"
                      onClick={() => onDelete(event)}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700"
                    >
                      <FaTrash />
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

export default function EventList(props) {
  return (
    <>
      <DesktopTable {...props} />
      <MobileCards {...props} />
    </>
  );
}